import { supabase } from "@/integrations/supabase/client";
import { createPost } from "./postsService";
import i18next from "i18next";

/**
 * Service to handle automatic posting of pet profile photos to the feed
 * This maintains separation of concerns while adding the auto-posting feature
 */

export interface CreateProfilePhotoPostParams {
  petId: string;
  petName: string;
  photoUrl: string;
  isNewPet?: boolean;
}

/**
 * Creates an automatic post when a pet profile photo is uploaded
 * @param params - Parameters for creating the profile photo post
 * @returns Promise resolving to the created post ID or null if creation failed
 */
export async function createProfilePhotoPost({
  petId,
  petName,
  photoUrl,
  isNewPet = false
}: CreateProfilePhotoPostParams): Promise<string | null> {
  try {
    console.log('📸 Creating profile photo post for pet:', petName);
    
    // Generate appropriate caption based on context
    const caption = isNewPet 
      ? i18next.t('postTypes.profilePhoto.newPetCaption', { petName })
      : i18next.t('postTypes.profilePhoto.updateCaption', { petName });

    // Create a media file entry for the profile photo
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user?.id) {
      console.error('📸 User not authenticated for profile photo post');
      return null;
    }

    // Create the post with special type 'profile_photo'
    const postData = {
      pet_id: petId,
      caption,
      privacy_setting: 'public' as const,
      post_type: 'profile_photo'
    };

    // Create post entry in memory_posts
    const { data: postResult, error: postError } = await supabase
      .from('memory_posts')
      .insert([{
        user_id: user.id,
        pet_id: petId,
        caption,
        privacy_setting: 'public',
        post_type: 'profile_photo'
      }])
      .select('id')
      .single();

    if (postError) {
      console.error('📸 Error creating profile photo post:', postError);
      return null;
    }

    // Create media file entry linking to the profile photo
    const { error: mediaError } = await supabase
      .from('media_files')
      .insert([{
        user_id: user.id,
        pet_id: petId,
        post_id: postResult.id,
        file_path: photoUrl, // Using the photo URL as file path
        mime_type: 'image/jpeg', // Default assumption for profile photos
        size_bytes: 0, // Unknown size for existing photos
        media_type: 'image_original',
        ai_generated: false,
        url: photoUrl
      }]);

    if (mediaError) {
      console.error('📸 Error linking media to profile photo post:', mediaError);
      // Post was created but media linking failed - not critical
    }

    console.log('📸 Profile photo post created successfully:', postResult.id);
    return postResult.id;
    
  } catch (error) {
    console.error('📸 Error in createProfilePhotoPost:', error);
    return null;
  }
}

/**
 * Checks if a profile photo post should be created
 * @param petId - ID of the pet
 * @param newPhotoUrl - New photo URL
 * @param oldPhotoUrl - Previous photo URL (if any)
 * @returns Boolean indicating if post should be created
 */
export function shouldCreateProfilePhotoPost(
  petId: string,
  newPhotoUrl: string | null | undefined,
  oldPhotoUrl: string | null | undefined
): boolean {
  // Don't create post if no new photo
  if (!newPhotoUrl) {
    return false;
  }
  
  // Create post if this is the first photo (no old photo)
  if (!oldPhotoUrl) {
    return true;
  }
  
  // Create post if photo URL changed (new photo uploaded)
  return newPhotoUrl !== oldPhotoUrl;
}